import React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { Progress } from 'reactstrap';
import Link from 'next/link';
import domainModel from 'domain-model';
import type { Cart } from 'domain-model/types/pcs';
import type { OrderFullfillmentCenterReference } from 'domain-model/types/oms';
import type {
  ShopConfig,
  CartProgressBarsColors,
} from 'domain-model/types/supplier-gateway';
// @ts-expect-error this file is in shop and purchasing respectively
// eslint-disable-next-line import/no-unresolved
import composeUrl from 'app/config/compose-url';
import type { PersistedMultiCartState } from '../../reducers/persisted-multi-cart.reducer';
import { shadeColor } from '../../utils/colorHelpers';

const { moneyFormatter } = domainModel;
// primary, info, success, random
const barColorsFallback = ['#619576', '#bcac76', '#9a6ee2', '#6464f2'];

export const CartProgressBar = ({
  cart,
  color = barColorsFallback[0] || '',
  minimumOrderRequirements,
}: {
  cart: Cart;
  color: string;
  minimumOrderRequirements: {
    type: string;
    minimum: number;
    localizedName: string;
    current: number;
    currency: string;
    isMinimumValueSatisfied: boolean;
    isPossibleToPlaceOrderBelowOrderMinimum: boolean;
  };
}) => {
  if (
    !minimumOrderRequirements ||
    minimumOrderRequirements.isMinimumValueSatisfied
  )
    return <></>;

  const orderFullfillmentCenter =
    cart.orderFullfillmentCenter as OrderFullfillmentCenterReference;
  let notSatisfiedMessage;
  let satisfiedMessage;
  const {
    type,
    minimum,
    localizedName,
    current,
    currency,
    isMinimumValueSatisfied,
    isPossibleToPlaceOrderBelowOrderMinimum,
  } = minimumOrderRequirements;

  if (type === 'minOrderValue') {
    notSatisfiedMessage = `${moneyFormatter.longForm(
      current,
      currency,
    )} av ${moneyFormatter.longForm(minimum, currency)}`;
    satisfiedMessage = `${localizedName} ${moneyFormatter.longForm(
      minimum,
      currency,
    )} uppnått`;
  } else {
    notSatisfiedMessage = `${current} kolli av ${minimum}`;
    satisfiedMessage = `${localizedName} ${minimum} uppnått`;
  }

  if (isPossibleToPlaceOrderBelowOrderMinimum) {
    notSatisfiedMessage += ' för fraktfritt';
    satisfiedMessage += ' för fraktfritt';
  }

  return (
    <div
      style={{ position: 'relative', height: '2rem' }}
      className="order-progress-bars"
    >
      <Progress
        multi
        style={{ height: '100%', borderRadius: '0' }}
        key="progress-multi-bar"
      >
        <Progress
          key={`progress-bar-${orderFullfillmentCenter.id}`}
          bar
          value={current * 100}
          style={{
            backgroundColor: color,
            borderRadius: 0,
          }}
        />
        <Progress
          key={`progress-bar-striped-${orderFullfillmentCenter.id}`}
          bar
          className="progress-bar-striped"
          value={isMinimumValueSatisfied ? 0 : (minimum - current) * 100}
          style={{
            backgroundColor: shadeColor(color, -25),
            borderRadius: '0',
          }}
        />
      </Progress>

      <div className="d-flex justify-content-between text-sm progress-bar-text-wrapper">
        <Link
          href={composeUrl('articleSearch', undefined, {
            '$articleBatches.orderFullfillmentCenterId$':
              orderFullfillmentCenter.id,
          })}
        >
          <span className="text-start ps-2  text-white">
            <strong>
              {orderFullfillmentCenter.name} {' - '}
              {isMinimumValueSatisfied ? satisfiedMessage : notSatisfiedMessage}
            </strong>
          </span>
        </Link>
      </div>
    </div>
  );
};

function CartProgressBars({
  persistedMultiCart,
  shopConfig,
}: {
  persistedMultiCart: PersistedMultiCartState;
  shopConfig: ShopConfig;
}) {
  const cartProgressBarsColors = (shopConfig.cartProgressBarsColors || {
    textColor: '#fff',
    headerColor: '#6464f2',
    barColors: barColorsFallback,
  }) as CartProgressBarsColors;

  const progressBars = _.chain(persistedMultiCart.carts)
    .map(cart => ({
      cart,
      minimumOrderRequirements: domainModel.cart.getMinimumOrderRequirements(
        cart,
        shopConfig,
      ),
    }))
    .filter(
      ({ cart, minimumOrderRequirements }) =>
        !_.isEmpty(cart.cartRows) &&
        !minimumOrderRequirements.isMinimumValueSatisfied,
    )
    .map(({ cart, minimumOrderRequirements }, index) => (
      <CartProgressBar
        key={cart.id}
        cart={cart}
        minimumOrderRequirements={minimumOrderRequirements}
        color={
          cartProgressBarsColors.barColors?.[index % 4] ||
          barColorsFallback[index % 4] ||
          ''
        }
      />
    ))
    .value();

  return (
    <div className="order-progress-bars progress-bar-on-top">
      {progressBars.length > 0 && (
        <>
          <Progress
            value="100"
            max="100"
            className="text-uppercase text-sm"
            style={{
              borderRadius: '0',
              height: '1.5rem',
              color: cartProgressBarsColors?.textColor || 'white',
            }}
            key="progress-bar-min-order-value"
            barStyle={{
              backgroundColor:
                cartProgressBarsColors?.headerColor || shadeColor('#6464f2', 5),
            }}
          >
            <strong>
              {progressBars.length === 1
                ? 'Minsta ordervärde'
                : 'Minsta ordervärden'}
            </strong>
          </Progress>
        </>
      )}
      {progressBars}
    </div>
  );
}

const mapStateToProps = ({
  persistedMultiCart,
  shopConfig,
}: {
  persistedMultiCart: PersistedMultiCartState;
  shopConfig: ShopConfig;
}) => ({
  persistedMultiCart,
  shopConfig,
});

export default connect(mapStateToProps)(CartProgressBars);
